import React, { useState } from 'react'
import BigNumber from 'bignumber.js'
import { Button, Flex, Text } from 'vvs-uikit'
import Tokens, { tokensByAddress } from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import { useAppDispatch } from 'state'
import { fetchFarmUserDataAsync } from 'state/farms'
import useToast from 'hooks/useToast'
import { getBalanceAmount, formatTokenAmount } from 'utils/formatBalance'
import { BIG_ZERO } from 'utils/bigNumber'
import { useWeb3React } from '@web3-react/core'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import LocalBalance from 'components/LocalBalance'
import useHarvestFarm from '../../hooks/useHarvestFarm'
import useHarvestFarmV2 from '../../hooks/useHarvestFarmV2'

interface FarmCardActionsProps {
  earnings?: BigNumber
  pid?: number
  version: number
  earningsV2?: any
  farms?: any[]
}

const HarvestAction: React.FC<FarmCardActionsProps> = ({ earnings, pid, version, earningsV2, farms }) => {
  const { account } = useWeb3React()
  const { toastSuccess, toastError } = useToast()
  const { t } = useTranslation()
  const [pendingTx, setPendingTx] = useState(false)
  const { onReward } = useHarvestFarm(pid)
  const { onReward: onRewardV2 } = useHarvestFarmV2(pid)
  const vvsPrice = usePriceVvsUsdc()
  const dispatch = useAppDispatch()
  let EnableHarvestButton = false
  const vvsRewards = account ? getBalanceAmount(earnings) : BIG_ZERO
  const vvsRewardsUsdc = vvsRewards ? vvsRewards.times(vvsPrice) : BIG_ZERO
  if (vvsRewardsUsdc.gt(0)) EnableHarvestButton = true

  const clickHarvestButton = async () => {
    setPendingTx(true)
    try {
      if (version === 2) {
        await onRewardV2()
        toastSuccess(
          t('Harvested!'),
          t('Your %symbol% earnings have been sent to your wallet!', { symbol: 'multi token' }),
        )
      } else {
        await onReward()
        toastSuccess(t('Harvested!'), t('Your %symbol% earnings have been sent to your wallet!', { symbol: 'VVS' }))
      }
    } catch (e) {
      toastError(
        t('Error'),
        t(
          'The network may be congested. Please try again and confirm the transaction. Do not change the default gas transactions settings for Cronos as it will not help to speed up.',
        ),
      )
      console.error(e)
    } finally {
      setPendingTx(false)
    }
    dispatch(fetchFarmUserDataAsync({ account, pids: [pid] }))
  }

  const tokenListByAddress = tokensByAddress()

  const renderEarningsV1 = (): JSX.Element => {
    const token = Tokens.vvs
    return (
      <Flex key={token.symbol} flexDirection="column" mt="6px">
        <Text fontSize="18px" bold>{`${token.symbol} ${formatTokenAmount(token, vvsRewards)}`}</Text>
        {vvsRewardsUsdc.gt(0) ? (
          <LocalBalance fontSize="12px" color="textSubtle" decimals={2} value={vvsRewardsUsdc} prefix="~" />
        ) : (
          <Text />
        )}
      </Flex>
    )
  }

  const renderEarningsV2 = (): JSX.Element => {
    return (
      <Flex flexDirection="column" mt="6px">
        {earningsV2.tokens.map((address: string, index: number) => {
          const token = tokenListByAddress[`${address}`]
          const pendingReward: BigNumber = getBalanceAmount(earningsV2.earnings[index], token.decimals)

          // token price
          let tokenPriceUsd: string = null
          if (address === Tokens.vvs.address) {
            tokenPriceUsd = vvsPrice.toFixed()
          } else {
            const getFarm = farms.find((farm) => farm.token.address === address)
            tokenPriceUsd = getFarm ? getFarm.tokenPriceUsdc : 'NaN'
          }

          // token price * amount
          const earningValue = pendingReward.times(tokenPriceUsd)
          if (earningValue.gt(0)) EnableHarvestButton = true

          return (
            <Flex key={address} flexDirection="column" mt="12px" mb="4px">
              <Text fontSize="18px" bold>{`${token.symbol} ${formatTokenAmount(token, pendingReward)}`}</Text>
              {earningValue.gt(0) ? (
                <LocalBalance fontSize="12px" color="textSubtle" decimals={2} value={earningValue} prefix="~" />
              ) : (
                <Text />
              )}
            </Flex>
          )
        })}
      </Flex>
    )
  }

  return (
    <Flex mb="8px" justifyContent="space-between" alignItems="start">
      {version === 2 && earningsV2 ? renderEarningsV2() : renderEarningsV1()}
      <Button disabled={!EnableHarvestButton || pendingTx} onClick={clickHarvestButton}>
        {t('Harvest')}
      </Button>
    </Flex>
  )
}

export default HarvestAction
